import React from "react";
import { Link } from "react-router-dom";
import { Bed, Bath, Wind, Users } from "lucide-react";
import { IMAGES } from "../lib/api";
import { useLanguage } from "../lib/i18n";

const Rooms = () => {
    const { t } = useLanguage();
    const ROOMS = [
        {
            name: "Master Bedroom",
            img: IMAGES.rooms[0],
            desc: "A serene king-bed sanctuary beneath a timber ceiling fan — soft linen throws, rattan headboard, hand-painted palm mural walls and sliding doors that open onto a private courtyard with a freestanding tub and outdoor shower under the palms.",
            specs: [t("bed.king"), t("spec.ensuite"), t("spec.outdoor"), t("spec.sleeps2")],
        },
        {
            name: "Middle Bedroom",
            img: IMAGES.rooms[1],
            desc: "Two twin beds side by side, rattan headboards, macrame wall hangings and a palm-print mural. The perfect middle room for friends, siblings, or a flexible set-up (beds can be joined on request).",
            specs: [t("bed.twin2"), t("spec.shared"), t("spec.acOnly"), t("spec.sleeps2")],
        },
        {
            name: "Pool View Bedroom",
            img: IMAGES.rooms[2],
            desc: "Full-height sliding doors open straight onto the pool deck. A queen bed with linen textures, hand-sketched palm walls and the water just steps from your feet — the easiest mornings of your life.",
            specs: [t("bed.queen"), t("spec.poolAccess"), t("spec.fanAc"), t("spec.sleeps2")],
        },
    ];

    return (
        <div data-testid="rooms-page" className="pt-32 pb-24 bg-background">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="overline text-secondary mb-4">{t("page.rooms.overline")}</div>
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light text-primary leading-[0.95] tracking-tight max-w-3xl">
                    {t("page.rooms.title")}
                </h1>
                <p className="mt-6 max-w-2xl text-muted-foreground font-light leading-relaxed">
                    {t("page.rooms.intro")}{" "}
                    <span className="text-primary">{t("page.rooms.entireHouse")}</span>
                </p>

                <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 border-y border-border py-8">
                    {[
                        { icon: Bed, label: t("label.bedrooms") },
                        { icon: Bath, label: t("label.bathrooms") },
                        { icon: Users, label: t("label.sleeps") },
                        { icon: Wind, label: t("label.ac") },
                    ].map((s, i) => (
                        <div
                            key={i}
                            className="flex items-center gap-3 text-foreground"
                        >
                            <s.icon size={18} strokeWidth={1.25} className="text-secondary shrink-0" />
                            <div className="text-sm font-light">{s.label}</div>
                        </div>
                    ))}
                </div>

                <div className="mt-20 space-y-32">
                    {ROOMS.map((r, i) => (
                        <div
                            key={r.name}
                            data-testid={`room-${i}`}
                            className={`grid lg:grid-cols-12 gap-10 items-center ${
                                i % 2 === 1 ? "lg:[&>:first-child]:order-2" : ""
                            }`}
                        >
                            <div className="lg:col-span-7 aspect-[4/3] overflow-hidden">
                                <img src={r.img} alt={r.name} className="w-full h-full object-cover" />
                            </div>
                            <div className="lg:col-span-5">
                                <div className="overline text-secondary mb-3">
                                    0{i + 1}
                                </div>
                                <h2 className="font-display text-4xl sm:text-5xl font-light text-primary leading-[1.05] tracking-tight">
                                    {r.name}
                                </h2>
                                <p className="mt-6 text-muted-foreground font-light leading-relaxed">
                                    {r.desc}
                                </p>
                                <div className="mt-8 flex flex-wrap gap-2">
                                    {r.specs.map((spec) => (
                                        <span
                                            key={spec}
                                            className="px-3 py-1.5 bg-muted text-foreground text-xs tracking-wider"
                                        >
                                            {spec}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-32 text-center">
                    <Link
                        to="/contact"
                        data-testid="rooms-cta"
                        className="inline-block px-12 py-4 bg-primary text-white text-xs tracking-[0.2em] uppercase hover:bg-secondary transition-colors"
                    >
                        {t("hero.getInTouch")}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Rooms;
